import { CliError, EXIT_INPUT } from "../../../common.js";
import type { ParsedBracketRecord, SourcePoint } from "./types.js";

export function authoringError(
  message: string,
  source: SourcePoint,
  received: string,
  next: string,
): CliError {
  return new CliError(message, {
           exitCode: EXIT_INPUT,
           errorCode: "AUTHORING_SOURCE_INVALID",
           hint: next,
           data: { actual: [`line ${source.line}, column ${source.column}: ${received}`] },
         });
}

function unescapePart(raw: string, source: SourcePoint, header: boolean): string {
  let result = "";
  for (let index = 0; index < raw.length; index += 1) {
    const char = raw[index]!;
    if (char !== "\\") {
      result += char;
      continue;
    }
    const next = raw[index + 1];
    if (next === undefined) {
      throw authoringError(
        "Trailing escape is invalid.",
        { line: source.line, column: source.column + index },
        raw,
        "Escape a supported character or remove the trailing backslash.",
      );
    }
    if (next === "\\" || next === "n" || (header && (next === "|" || next === "]"))) {
      result += next === "n" ? "\n" : next;
      index += 1;
      continue;
    }
    throw authoringError(
      "Unknown escape sequence.",
      { line: source.line, column: source.column + index },
      `\\${next}`,
      header ? "Use only \\|, \\], \\\\, or \\n in a header." : "Use only \\\\ or \\n in record content.",
    );
  }
  return result;
}

export function parseBracketRecord(raw: string, line: number): ParsedBracketRecord {
  const source = { line, column: Math.max(1, raw.indexOf("[") + 1) };
  const text = raw.trimStart();
  if (!text.startsWith("[")) {
    throw authoringError(
      "Expected a bracket record.",
      source,
      raw,
      "Start the semantic line with a known [type] record.",
    );
  }
  let closing = -1;
  let escaped = false;
  for (let index = 1; index < text.length; index += 1) {
    const char = text[index]!;
    if (escaped) {
      escaped = false;
      continue;
    }
    if (char === "\\") {
      escaped = true;
      continue;
    }
    if (char === "]") {
      closing = index;
      break;
    }
  }
  if (closing < 0) {
    throw authoringError("Bracket record is not closed.", source, raw, "Add the closing ].");
  }
  const headerRaw = text.slice(1, closing);
  const rawSlots: string[] = [];
  let current = "";
  escaped = false;
  for (let index = 0; index < headerRaw.length; index += 1) {
    const char = headerRaw[index]!;
    if (escaped) {
      current += `\\${char}`;
      escaped = false;
    } else if (char === "\\") {
      escaped = true;
    } else if (char === "|") {
      rawSlots.push(current);
      current = "";
    } else {
      current += char;
    }
  }
  if (escaped) current += "\\";
  rawSlots.push(current);
  const [rawType, ...rest] = rawSlots;
  const type = unescapePart(rawType ?? "", source, true);
  if (!type) {
    throw authoringError("Record type is empty.", source, raw, "Use a known record type.");
  }
  const after = text.slice(closing + 1);
  const contentRaw = after.startsWith(" ") ? after.slice(1) : after;
  return {
    type,
    slots: rest.map((slot) => unescapePart(slot, source, true)),
    content: unescapePart(contentRaw, { line, column: closing + 2 }, false),
    source,
  };
}
